import { keepPreviousData, useQuery } from "@tanstack/react-query";
import {
	createFileRoute,
	useNavigate,
	useParams,
	useSearch,
} from "@tanstack/react-router";
import { Building01 } from "@untitledui/icons";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { Key } from "react-aria-components";

import type { DevtoolsBoundingBox } from "@/components/base/map/MapBoxComponent.types";
import type { SelectItemType } from "@/components/base/select/select";
import { SelectItem } from "@/components/base/select/select-item";
import { useAppForm } from "@/components/form";
import { MapFilter } from "@/components/Molecules/Map/Filter";
import { getConfig } from "@/config";
import {
	areDevtoolsBoundsEqual,
	buildSubOrgMvtFilters,
	devtoolsBoundsToBoundingBox,
	isMapLegendInBoundsCountLoading,
	MapShell,
	MapShellVariant,
	type OrgMapSearch,
	orgMapSearchSchema,
	orgMvtTooltipForLayer,
	orgOverviewMappableCountParams,
	parseBbox,
	replaceMapSearchInHistory,
	resolveMvtTileViewCacheKey,
	ServerMvtLayerId,
	subOrgIdsFromSearchParam,
} from "@/features/map";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { useDebounced } from "@/hooks/use-debounced";
import { useOrganizationHasSubOrganizations } from "@/hooks/use-organization-has-sub-organizations";
import { assetQueryOptions } from "@/services/asset";
import { jobQueryOptions } from "@/services/job";
import {
	organizationQueryOptions,
	SUB_ORGANIZATION_MAX_PAGE_SIZE,
} from "@/services/organization";
import { recordInstanceQueryOptions } from "@/services/record";
import { ticketQueryOptions } from "@/services/ticket";

/** Sentinel id for "All" in the sub-org filter (org-level cluster set). */
const ALL_SUB_ORGANIZATIONS_ID = "__all__";

export type MapSearch = OrgMapSearch;

export const Route = createFileRoute(
	"/_authenticated/(web-app)/_web/web/$orgId/map",
)({
	component: RouteComponent,
	validateSearch: (search): MapSearch => orgMapSearchSchema.parse(search),
});

const MAP_ROUTE_ID = "/_authenticated/(web-app)/_web/web/$orgId/map";

/** Sub-organization filter dropdown using TanStack Form + SelectField. */
function MapSubOrgFilter({
	selectedKeys,
	onSelectionChange,
	items,
	isPending,
	allOptionId,
}: {
	selectedKeys: Set<Key>;
	onSelectionChange: (keys: Set<Key>) => void;
	items: SelectItemType[];
	isPending: boolean;
	allOptionId: string;
}) {
	const { t } = useAppTranslation();
	const defaultKey =
		selectedKeys.size === 0 ||
		(selectedKeys.size === 1 && selectedKeys.has(allOptionId))
			? allOptionId
			: (Array.from(selectedKeys)[0] as string);

	const form = useAppForm({
		defaultValues: { subOrgKey: defaultKey },
		onSubmit: () => {},
	});

	const handleItemSelect = useCallback(
		(key: string | null) => {
			const next = new Set<Key>([key ?? allOptionId]);
			onSelectionChange(next);
		},
		[onSelectionChange, allOptionId],
	);

	if (isPending) {
		return (
			<span className="text-sm text-tertiary">
				{t("main.map.subOrgFilter.loadingSubOrganizations")}
			</span>
		);
	}

	return (
		<div className="flex h-full flex-wrap items-center gap-3" key={defaultKey}>
			<div className="min-w-0 flex-1">
				<form.AppField name="subOrgKey">
					{(field) => (
						<field.SelectField
							label={t("admin.nav.items.subOrganizations.label")}
							placeholder={t("main.jobs.modal.placeholders.subOrganization")}
							placeholderIcon={Building01}
							items={items}
							size="sm"
							showSupportingTextInValue={false}
							onItemSelect={handleItemSelect}
						>
							{(item: SelectItemType) => (
								<SelectItem id={item.id} icon={item.icon} label={item.label}>
									{item.label ?? item.id}
								</SelectItem>
							)}
						</field.SelectField>
					)}
				</form.AppField>
			</div>
		</div>
	);
}

/**
 * Org overview map — Destiny {@link MapShell} with org-wide MVT layers.
 */
function RouteComponent() {
	const { t } = useAppTranslation();
	const config = getConfig();
	const accessToken = config?.integrations?.mapbox?.publicAccessToken;
	const params = useParams({ strict: false });
	const orgId = params.orgId ?? "";
	const { hasSubOrganizations } = useOrganizationHasSubOrganizations(orgId);
	const navigate = useNavigate();
	const search = useSearch({ from: MAP_ROUTE_ID });

	const [viewportBounds, setViewportBounds] =
		useState<DevtoolsBoundingBox | null>(null);

	const onViewportBoundsChange = useCallback((bounds: DevtoolsBoundingBox) => {
		setViewportBounds((prev) =>
			prev != null && areDevtoolsBoundsEqual(prev, bounds) ? prev : bounds,
		);
	}, []);

	const effectiveSubOrgIds = useMemo(
		() => subOrgIdsFromSearchParam(search.subOrg),
		[search.subOrg],
	);

	const selectedKeysFromSearch = useMemo(() => {
		if (effectiveSubOrgIds.length === 0) {
			return new Set<Key>([ALL_SUB_ORGANIZATIONS_ID]);
		}
		return new Set<Key>(effectiveSubOrgIds);
	}, [effectiveSubOrgIds]);

	const [selectedKeys, setSelectedKeys] = useState<Set<Key>>(
		() => selectedKeysFromSearch,
	);

	useEffect(() => {
		setSelectedKeys(selectedKeysFromSearch);
	}, [selectedKeysFromSearch]);

	const { data: subOrganizations = [], isPending } = useQuery({
		...organizationQueryOptions.subOrganizations.list(
			orgId,
			1,
			SUB_ORGANIZATION_MAX_PAGE_SIZE,
		),
		enabled: !!orgId && hasSubOrganizations,
	});

	const countSubOrgFilter = useMemo(() => {
		if (effectiveSubOrgIds.length === 0) return undefined;
		return effectiveSubOrgIds[0];
	}, [effectiveSubOrgIds]);

	const boundingBox = useMemo(
		() => devtoolsBoundsToBoundingBox(viewportBounds ?? parseBbox(search.bbox)),
		[search.bbox, viewportBounds],
	);

	const debouncedBoundingBox = useDebounced(boundingBox, 500);

	const overviewTotalCountParams = useMemo(
		() =>
			orgOverviewMappableCountParams({
				organizationId: orgId,
				subOrganizationId: countSubOrgFilter,
			}),
		[countSubOrgFilter, orgId],
	);

	const overviewInBoundsCountParams = useMemo(
		() =>
			orgOverviewMappableCountParams({
				organizationId: orgId,
				subOrganizationId: countSubOrgFilter,
				boundingBox: debouncedBoundingBox,
			}),
		[countSubOrgFilter, debouncedBoundingBox, orgId],
	);

	const { data: jobsCount, isPending: jobsTotalPending } = useQuery(
		jobQueryOptions.count(overviewTotalCountParams),
	);

	const { data: ticketsCount, isPending: ticketsTotalPending } = useQuery(
		ticketQueryOptions.count(overviewTotalCountParams),
	);

	const { data: assetsCount, isPending: assetsTotalPending } = useQuery(
		assetQueryOptions.assetsCount(overviewTotalCountParams),
	);

	const { data: recordsCount, isPending: recordsTotalPending } = useQuery(
		recordInstanceQueryOptions.count(overviewTotalCountParams),
	);

	const hasViewportBounds = boundingBox != null;

	const { data: jobsCountInBounds, isPending: jobsCountInBoundsPending } =
		useQuery({
			...jobQueryOptions.count(overviewInBoundsCountParams),
			enabled: !!orgId && debouncedBoundingBox != null,
			placeholderData: keepPreviousData,
		});

	const { data: ticketsCountInBounds, isPending: ticketsCountInBoundsPending } =
		useQuery({
			...ticketQueryOptions.count(overviewInBoundsCountParams),
			enabled: !!orgId && debouncedBoundingBox != null,
			placeholderData: keepPreviousData,
		});

	const { data: assetsCountInBounds, isPending: assetsCountInBoundsPending } =
		useQuery({
			...assetQueryOptions.assetsCount(overviewInBoundsCountParams),
			enabled: !!orgId && debouncedBoundingBox != null,
			placeholderData: keepPreviousData,
		});

	const { data: recordsCountInBounds, isPending: recordsCountInBoundsPending } =
		useQuery({
			...recordInstanceQueryOptions.count(overviewInBoundsCountParams),
			enabled: !!orgId && debouncedBoundingBox != null,
			placeholderData: keepPreviousData,
		});

	const subOrganizationItems: SelectItemType[] = useMemo(
		() => [
			{
				id: ALL_SUB_ORGANIZATIONS_ID,
				label: t("common.buttonGroup.all"),
				icon: Building01,
			},
			...subOrganizations
				.filter((s): s is { id: string; name: string | null } => s.id != null)
				.map((s) => ({
					id: s.id,
					label: s.name ?? s.id,
					icon: Building01,
				})),
		],
		[subOrganizations, t],
	);

	const onSubOrgSelectionChange = useCallback(
		(keys: Set<Key>) => {
			let next: Set<Key>;
			if (keys.size === 0) {
				next = new Set([ALL_SUB_ORGANIZATIONS_ID]);
			} else if (keys.has(ALL_SUB_ORGANIZATIONS_ID) && keys.size > 1) {
				next = new Set([...keys].filter((k) => k !== ALL_SUB_ORGANIZATIONS_ID));
			} else {
				next = keys;
			}
			setSelectedKeys(next);
			const subOrgIds = Array.from(next).filter(
				(k): k is string => k !== ALL_SUB_ORGANIZATIONS_ID,
			);
			const subOrg = subOrgIds.length === 0 ? undefined : subOrgIds.join(",");
			const nextSearch = {
				...search,
				subOrg,
			};
			void navigate({
				to: "/web/$orgId/map",
				params: { orgId },
				search: nextSearch,
				replace: true,
			});
			// Keep `subOrg` in the address bar (Router may omit optional keys).
			replaceMapSearchInHistory(nextSearch);
		},
		[navigate, orgId, search],
	);

	const mvtExtraFilters = useMemo(() => {
		const filters = buildSubOrgMvtFilters(effectiveSubOrgIds);
		return {
			cluster: filters.clusterFilter,
			feature: filters.featureFilter,
		};
	}, [effectiveSubOrgIds]);

	const mvtViewCacheKey = useMemo(
		() => resolveMvtTileViewCacheKey(effectiveSubOrgIds),
		[effectiveSubOrgIds],
	);

	const overviewLegendStats = useMemo(
		() => ({
			[ServerMvtLayerId.Jobs]: {
				count: jobsCountInBounds,
				totalCount: jobsCount,
				// Wait for first in-bounds data; keep prior count while refetching.
				countLoading: isMapLegendInBoundsCountLoading({
					totalPending: jobsTotalPending,
					hasViewportBounds,
					inBoundsPending: jobsCountInBoundsPending,
				}),
			},
			[ServerMvtLayerId.Tickets]: {
				count: ticketsCountInBounds,
				totalCount: ticketsCount,
				countLoading: isMapLegendInBoundsCountLoading({
					totalPending: ticketsTotalPending,
					hasViewportBounds,
					inBoundsPending: ticketsCountInBoundsPending,
				}),
			},
			[ServerMvtLayerId.Assets]: {
				count: assetsCountInBounds,
				totalCount: assetsCount,
				countLoading: isMapLegendInBoundsCountLoading({
					totalPending: assetsTotalPending,
					hasViewportBounds,
					inBoundsPending: assetsCountInBoundsPending,
				}),
			},
			[ServerMvtLayerId.Records]: {
				count: recordsCountInBounds,
				totalCount: recordsCount,
				countLoading: isMapLegendInBoundsCountLoading({
					totalPending: recordsTotalPending,
					hasViewportBounds,
					inBoundsPending: recordsCountInBoundsPending,
				}),
			},
		}),
		[
			assetsCount,
			assetsCountInBounds,
			assetsCountInBoundsPending,
			assetsTotalPending,
			hasViewportBounds,
			jobsCount,
			jobsCountInBounds,
			jobsCountInBoundsPending,
			jobsTotalPending,
			recordsCount,
			recordsCountInBounds,
			recordsCountInBoundsPending,
			recordsTotalPending,
			ticketsCount,
			ticketsCountInBounds,
			ticketsCountInBoundsPending,
			ticketsTotalPending,
		],
	);

	if (!accessToken) {
		return (
			<div className="flex min-h-[400px] flex-col items-center justify-center gap-4 p-8">
				<h1 className="text-2xl font-bold text-primary">
					{t("main.nav.items.map.label")}
				</h1>
				<p className="max-w-md text-center text-tertiary">
					{t("main.map.mapUnavailable")}
				</p>
			</div>
		);
	}

	return (
		<MapShell
			variant={MapShellVariant.Overview}
			accessToken={accessToken}
			orgId={orgId}
			aria-label={t("main.map.overview.title")}
			data-testid="org-overview-map"
			mvtExtraFilters={mvtExtraFilters}
			mvtViewCacheKey={mvtViewCacheKey}
			overviewLegendStats={overviewLegendStats}
			overviewSubOrganizationId={countSubOrgFilter}
			resolveOverviewMvtTooltip={orgMvtTooltipForLayer}
			onViewportBoundsChange={onViewportBoundsChange}
			filterContent={
				hasSubOrganizations ? (
					<MapFilter primaryText={t("map.filters")}>
						<MapSubOrgFilter
							selectedKeys={selectedKeys}
							onSelectionChange={onSubOrgSelectionChange}
							items={subOrganizationItems}
							isPending={isPending}
							allOptionId={ALL_SUB_ORGANIZATIONS_ID}
						/>
					</MapFilter>
				) : undefined
			}
		/>
	);
}
